import COLORS from 'constants/colors';

export const overlayControls = {
  OVERLAY_QUICK_MENU_MAX: 5
};

export const FIELD_VIEW_PLUS_URL = 'https://spatial.fieldviewplus.com';

export const overlayLayerToFieldViewPlusMapType = {
  'yield': 'yield',
  'moisture': 'moisture',
  'combine_speed': 'combinespeed',
  'actual_rate': 'actualpopulation',
  'planter_speed': 'planterspeed',
  'elevation': 'elevation'
};

export const fieldHealthLayers = [
  'VEGETATION',
  'TRUE_COLOR',
  'SCOUTING'
].reduce((result, key) => { result[key] = `${key.toLowerCase()}`; return result; }, {});

export const fieldHealthLayersTags = {
  [fieldHealthLayers.SCOUTING]: 'rank', 
  [fieldHealthLayers.TRUE_COLOR]: 'true-color',
  [fieldHealthLayers.VEGETATION]: 'abs'
};

export const soilSampleLayers = [
  'SOIL_SAMPLE_CEC',
  'SOIL_SAMPLE_EC',
  'SOIL_SAMPLE_K',
  'SOIL_SAMPLE_OM',
  'SOIL_SAMPLE_P',
  'SOIL_SAMPLE_PH'
].reduce((result, key) => { result[key] = `${key.toLowerCase()}`; return result; }, {});

export const nonSatLayers = [
  'OTHER'
].reduce((result, key) => { result[key] = `${key.toLowerCase()}`; return result; }, {});

export const ssurgoLayers = [
  'SOIL'
].reduce((result, key) => { result[key] = `${key.toLowerCase()}`; return result; }, {});

export const importedMapsLayers = [
  'IMPORTED_MAPS'
].reduce((result, key) => { result[key] = `${key.toLowerCase()}`; return result; }, {});

export const fieldViewLayers = [
  'YIELD',
  'MOISTURE',
  'COMBINE_SPEED',
  'LIQUID',
  'ACTUAL_RATE',
  'ELEVATION',
  'HYBRID',
  'PLANTER_SPEED'
].reduce((result, key) => { result[key] = `${key.toLowerCase()}`; return result; }, {});

export const scriptingLayers = [
  'PRODUCT_RATE'
].reduce((result, key) => { result[key] = `${key.toLowerCase()}`; return result; }, {});

export const knownButUnusedLayers = [
  // Below come in from mosaic and are identified, but not used
  'RAW',
  'MOSAIC_YIELD'
].reduce((result, key) => { result[key] = `${key.toLowerCase()}`; return result; }, {});

export const overlayLayers = {
  ...scriptingLayers,
  ...ssurgoLayers,
  ...soilSampleLayers,
  ...fieldHealthLayers,
  ...fieldViewLayers,
  ...nonSatLayers,
  ...knownButUnusedLayers,
  ...importedMapsLayers
};

export const overlayLayerGroups = [
  {
    type: 'baseMaps',
    layers: [
      overlayLayers.SOIL,
      overlayLayers.SOIL_SAMPLE_CEC,
      overlayLayers.SOIL_SAMPLE_EC,
      overlayLayers.SOIL_SAMPLE_K,
      overlayLayers.SOIL_SAMPLE_OM,
      overlayLayers.SOIL_SAMPLE_P,
      overlayLayers.SOIL_SAMPLE_PH
    ]
  },
  {
    type: 'seasonMaps',
    layers: [
      overlayLayers.VEGETATION,
      overlayLayers.TRUE_COLOR,
      overlayLayers.SCOUTING,
      overlayLayers.LIQUID,
      overlayLayers.OTHER
    ]
  },
  { type: 'harvestMaps',
    layers: [
      overlayLayers.YIELD,
      overlayLayers.MOISTURE,
      overlayLayers.COMBINE_SPEED
    ]
  },
  {
    type: 'plantingMaps',
    layers: [
      overlayLayers.ACTUAL_RATE,
      overlayLayers.HYBRID,
      overlayLayers.PLANTER_SPEED,
      overlayLayers.ELEVATION
    ]
  }
];

export const fieldViewLayerMappings = {
  [overlayLayers.ACTUAL_RATE]: 'actualpopulation',
  [overlayLayers.YIELD]: 'yield',
  [overlayLayers.MOISTURE]: 'moisture',
  [overlayLayers.COMBINE_SPEED]: 'combinespeed',
  [overlayLayers.HYBRID]: 'hybrid',
  [overlayLayers.PLANTER_SPEED]: 'planterspeed',
  [overlayLayers.ELEVATION]: 'elevation'
};

export const fieldViewLayerServiceKeys = { // which layers are in which FV service type, distinct from layer groups
  harvest: [
    overlayLayers.YIELD,
    overlayLayers.MOISTURE,
    overlayLayers.COMBINE_SPEED
  ],
  liquid: [ overlayLayers.LIQUID ],
  planting: [
    overlayLayers.ACTUAL_RATE,
    overlayLayers.HYBRID,
    overlayLayers.PLANTER_SPEED,
    overlayLayers.ELEVATION
  ]
};

export const fieldViewLayerServiceKeysInverted = Object.entries(fieldViewLayerServiceKeys).reduce(
  (all, [fieldViewServiceKey, overlayLayers]) => {
    overlayLayers.forEach( layer => {
      all[layer] = fieldViewServiceKey;
    });
    return all;
  }, {});

export const overlayLegends = { // TODO: Move inside parser
  [overlayLayers.SCOUTING]: {
    labelKey: `label-text__overlay-legend--${overlayLayers.SCOUTING}`,
    layer: overlayLayers.SCOUTING,
    colors: COLORS[overlayLayers.SCOUTING]
  },
  [overlayLayers.VEGETATION]: {
    labelKey: `label-text__overlay-legend--${overlayLayers.VEGETATION}`,
    layer: overlayLayers.VEGETATION,
    colors: COLORS[overlayLayers.VEGETATION]
  },
  [overlayLayers.SOIL]: {
    labelKey: `label-text__overlay-legend--${overlayLayers.SOIL}`,
    layer: overlayLayers.SOIL,
    colors: COLORS[overlayLayers.SOIL]
  }
};

export const allOverlayQuickSelectMenuOptions = [
  overlayLayers.PRODUCT_RATE,
  overlayLayers.VEGETATION,
  overlayLayers.YIELD,
  overlayLayers.SOIL_SAMPLE_OM,
  overlayLayers.SOIL_SAMPLE_CEC,
  overlayLayers.SOIL_SAMPLE_P,
  overlayLayers.SOIL_SAMPLE_K,
  overlayLayers.ACTUAL_RATE,
  overlayLayers.HYBRID,
  overlayLayers.SOIL
];

export const seasonlessOverlayLayers = [
  overlayLayers.SOIL,
  overlayLayers.PRODUCT_RATE
].reduce((result, key) => { result[key] = key; return result; }, {});

export const datelessOverlayLayers = [
  overlayLayers.ACTUAL_RATE,
  overlayLayers.COMBINE_SPEED,
  overlayLayers.ELEVATION,
  overlayLayers.HYBRID,
  overlayLayers.MOISTURE,
  overlayLayers.MOISTURE,
  overlayLayers.PLANTER_SPEED,
  overlayLayers.PRODUCT_RATE,
  overlayLayers.SOIL,
  overlayLayers.YIELD
].reduce((result, key) => { result[key] = key; return result; }, {});
